import { takeEvery, put, call, select, all } from 'redux-saga/effects';
import { Api } from 'api';
import { callApi } from 'root/appSaga';
import * as Sentry from '@sentry/react';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as companyActions from 'features/company/companyActions';
import { getCompany } from 'features/company/companyDetails';
import { ActionParams, Company, User } from 'types';
import { ModalEnum } from 'myParkdUtils/enums';
import { getModalContentAuthorizationForbidden } from 'myParkdUtils/basicMessageModalContent';
import { authSlice } from 'features/auth/authSlice';
import { getUser } from 'features/user/userSlice';
import { setSentryScope } from 'sentryTools';


export function* authSaga(): any {
    return yield all([
        takeEvery(authSlice.actions.login, authorize),
        takeEvery(authSlice.actions.resetPassword, resetPassword),
        takeEvery(authSlice.actions.changePassword, changePassword),
        takeEvery((action: any) => new RegExp('.*_ERROR').test(action.type), checkAuthentication)
    ]);
}

export function* checkAuthentication(params: ActionParams) {
    if (params.error.status === 401 && params.type !== 'AUTH_LOGIN_ERROR' && params.type !== 'AUTH_CHANGE_PASSWORD_ERROR') {
        yield put(authSlice.actions.logoutCompleted()); // todo: add meaningfull message for user
    }

    const forbiddenErrorActions = ['COMPANY_REQUEST_ERROR', 'COMPANY_VEHICLES_REQUEST_ERROR', 'HISTORICAL_SESSIONS_REQUEST_ERROR'];
    if (params.error.status === 403 && params.type && forbiddenErrorActions.includes(params.type)) {
        const user: User = yield select(getUser);
        Sentry.captureMessage("User without fleetmanager role was logged into MyParkd");
        yield put(
            modalWindowActions.updateModalWindow(ModalEnum.BASIC_MESSAGE_MODAL, {
                modalContent: getModalContentAuthorizationForbidden(user.email),
            })
        );
        //yield put(authActions.logoutCompleted());
    }
}

export function* logoutSaga(params: any) {
    yield put(authSlice.actions.logoutCompleted());
    // Scope.clear() was removed in Sentry v11: unset what setSentryScope() added
    Sentry.getCurrentScope()
        .setUser(null)
        .setTags({ companyId: undefined, companyName: undefined });
}

export function* changePassword(params: ReturnType<typeof authSlice.actions.changePassword>) {
    yield select();
    try {
        const { oldPassword, newPassword } = params.payload;
        yield callApi(Api.updatePassword, oldPassword, newPassword);
        yield put(authSlice.actions.changePasswordCompleted());
    } catch (error) {
        yield put(authSlice.actions.changePasswordError({ error }));
    }
}

export function* resetPassword(params: ReturnType<typeof authSlice.actions.resetPassword>) {
    yield select();
    try {
        yield call([Api, Api.resetPassword], params?.payload?.email);
        yield put(authSlice.actions.resetPasswordCompleted());
    } catch (error) {
        yield put(authSlice.actions.resetPasswordError({ error }));
    }
}

export function* authorize(params: ReturnType<typeof authSlice.actions.login>) {
    Sentry.addBreadcrumb({ category: 'authSaga', message: 'authorize()' });
    try {
        const { email, password } = params.payload;
        const { token, user } = yield call([Api, Api.login], email, password);

        yield put(authSlice.actions.loginCompleted({ user, token }));
        yield put(companyActions.getCompanyDetails());
        yield put(companyActions.getCompanyFeaturePermissions());
        const company: Company = yield select(getCompany);

        setSentryScope(token, user, company);

        return token;
    } catch (error) {
        yield put(authSlice.actions.loginError({ error }));
    }
}
